import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { buildAssigneeChartData } from './chartSeries.js';
import { outOfOfficeAssignmentSummary, outOfOfficeAssignmentWarning } from '../utils/outOfOffice.js';
import '../common/modals.css';
import './DocumentAssignmentsChart.css';

export const ASSIGNMENT_CHART_CLICK_HINT = 'Click a bar for a further breakdown of documents by day.';

export const getAssigneeIdFromChartClick = (data) =>
  data?.activePayload?.[0]?.payload?.assigneeId
  || data?.payload?.assigneeId
  || data?.assigneeId
  || null;

const USER_BAR_COLOR = '#3498db';
const TEAM_BAR_COLOR = '#9b59b6';

const toLocalDayKey = (millis) => {
  const date = new Date(millis);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDayLabel = (dayKey) => {
  const [year, month, day] = dayKey.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
  });
};

const buildDailyDistribution = (documents) => {
  const countsByDay = {};
  documents.forEach((doc) => {
    const timestamp = Number(doc.timestamp);
    if (!Number.isFinite(timestamp) || timestamp <= 0) {
      return;
    }
    const dayKey = toLocalDayKey(timestamp);
    countsByDay[dayKey] = (countsByDay[dayKey] || 0) + 1;
  });

  const dayKeys = Object.keys(countsByDay).sort();
  if (dayKeys.length === 0) {
    return [];
  }

  const [startYear, startMonth, startDay] = dayKeys[0].split('-').map(Number);
  const [endYear, endMonth, endDay] = dayKeys[dayKeys.length - 1].split('-').map(Number);
  const cursor = new Date(startYear, startMonth - 1, startDay);
  const end = new Date(endYear, endMonth - 1, endDay);
  const result = [];

  while (cursor <= end) {
    const dayKey = toLocalDayKey(cursor.getTime());
    result.push({
      day: dayKey,
      label: formatDayLabel(dayKey),
      count: countsByDay[dayKey] || 0,
    });
    cursor.setDate(cursor.getDate() + 1);
  }

  return result;
};

export const AssigneeDailyDistributionModal = ({ assignee, documents, onClose }) => {
  const dailyData = useMemo(() => buildDailyDistribution(documents), [documents]);
  const barColor = assignee.isTeam ? TEAM_BAR_COLOR : USER_BAR_COLOR;

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    <div className="modal-overlay assignment-day-modal-overlay" onClick={onClose}>
      <div
        className="modal-container modal-animated assignment-day-modal"
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal-header">
          <h2>Documents assigned to {assignee.name} by day</h2>
          <button type="button" className="close-button" onClick={onClose} aria-label="Close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="modal-content">
          {dailyData.length === 0 ? (
            <div className="no-data">No date data available for this assignee</div>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart
                data={dailyData}
                margin={{ top: 20, right: 30, left: 20, bottom: dailyData.length > 7 ? 60 : 20 }}
                isAnimationActive={false}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="label"
                  angle={dailyData.length > 7 ? -45 : 0}
                  textAnchor={dailyData.length > 7 ? 'end' : 'middle'}
                  height={dailyData.length > 7 ? 80 : 30}
                  interval={dailyData.length > 14 ? 'preserveStartEnd' : 0}
                />
                <YAxis allowDecimals={false} />
                <Tooltip
                  formatter={(value) => [value, 'Documents']}
                  labelFormatter={(label) => label}
                />
                <Bar dataKey="count" name="Documents" fill={barColor} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export const DocumentAssignmentsByAssigneeChart = React.memo(({
  documentsAssigned,
  userIdToName = {},
  teams = [],
  activeUsers = [],
}) => {
  const [selectedAssigneeId, setSelectedAssigneeId] = useState(null);

  const assignedDocuments = useMemo(
    () => (documentsAssigned || []).filter((doc) => doc.assigneeId),
    [documentsAssigned]
  );

  const chartData = useMemo(
    () => buildAssigneeChartData(documentsAssigned, userIdToName, teams),
    [documentsAssigned, userIdToName, teams]
  );

  const selectedAssignee = useMemo(
    () => chartData.find((entry) => entry.assigneeId === selectedAssigneeId) || null,
    [chartData, selectedAssigneeId]
  );

  const selectedDocuments = useMemo(
    () => assignedDocuments.filter((doc) => doc.assigneeId === selectedAssigneeId),
    [assignedDocuments, selectedAssigneeId]
  );

  const awayWarning = useMemo(() => {
    const summary = outOfOfficeAssignmentSummary(assignedDocuments, activeUsers);
    return outOfOfficeAssignmentWarning(summary);
  }, [assignedDocuments, activeUsers]);

  const handleBarClick = useCallback((data) => {
    const assigneeId = getAssigneeIdFromChartClick(data);
    if (assigneeId) {
      setSelectedAssigneeId(assigneeId);
    }
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedAssigneeId(null);
  }, []);

  if (documentsAssigned == null) {
    return <div className="no-data">Loading assignment data...</div>;
  }

  if (chartData.length === 0) {
    return <div className="no-data">No documents are currently assigned</div>;
  }

  return (
    <div>
      {awayWarning && (
        <div className="assignment-ooo-warning">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <span>{awayWarning}</span>
        </div>
      )}
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          isAnimationActive={false}
          style={{ cursor: 'pointer' }}
          onClick={handleBarClick}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="assignee" />
          <YAxis allowDecimals={false} />
          <Tooltip
            formatter={(value) => [value, 'Documents assigned']}
            labelFormatter={(label) => label}
            cursor={{ fill: 'rgba(0, 0, 0, 0.06)', cursor: 'pointer' }}
          />
          <Bar
            dataKey="count"
            name="Documents assigned"
            cursor="pointer"
            background={{ fill: 'transparent', cursor: 'pointer' }}
            onClick={handleBarClick}
            isAnimationActive={false}
          >
            {chartData.map((entry) => (
              <Cell
                key={entry.assigneeId}
                fill={entry.isTeam ? TEAM_BAR_COLOR : USER_BAR_COLOR}
                cursor="pointer"
                onClick={() => setSelectedAssigneeId(entry.assigneeId)}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {selectedAssignee && (
        <AssigneeDailyDistributionModal
          assignee={selectedAssignee}
          documents={selectedDocuments}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
});
